import React, { useState, useEffect } from 'react';
import { FaTimes, FaEdit, FaTrash, FaClock, FaUser, FaComments, FaPaperclip, FaPlay, FaStop } from 'react-icons/fa';
import api from '../../../services/api';

const TaskDetailsModal = ({ taskId, onClose, onEdit, onDeleted, onUpdate }) => {
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details'); // 'details', 'comments', 'attachments', 'time'

  const [newComment, setNewComment] = useState('');
  const [isTracking, setIsTracking] = useState(false);
  const [activeTimeEntry, setActiveTimeEntry] = useState(null);

  useEffect(() => {
    fetchTaskDetails();
  }, [taskId]);

  const fetchTaskDetails = async () => {
    try {
      const [taskRes, commentsRes, attachmentsRes, timeRes] = await Promise.all([
        api.get(`/portal/tasks/${taskId}`),
        api.get(`/portal/tasks/${taskId}/comments`),
        api.get(`/portal/tasks/${taskId}/attachments`),
        api.get(`/portal/tasks/${taskId}/time`)
      ]);

      setTask(taskRes.data);
      setComments(commentsRes.data);
      setAttachments(attachmentsRes.data);
      setTimeEntries(timeRes.data);

      // Check if there's an active time entry
      const active = timeRes.data.find(entry => !entry.end_time);
      if (active) {
        setIsTracking(true);
        setActiveTimeEntry(active);
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await api.delete(`/portal/tasks/${taskId}`);
      onDeleted();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await api.post(`/portal/tasks/${taskId}/comments`, { content: newComment });
      setNewComment('');
      fetchTaskDetails();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    }
  };

  const handleStartTimer = async () => {
    try {
      const res = await api.post(`/portal/tasks/${taskId}/time/start`);
      setIsTracking(true);
      setActiveTimeEntry(res.data);
      fetchTaskDetails();
    } catch (error) {
      console.error('Error starting timer:', error);
      alert('Failed to start timer');
    }
  };

  const handleStopTimer = async () => {
    if (!activeTimeEntry) return;

    try {
      await api.put(`/portal/tasks/${taskId}/time/${activeTimeEntry.id}/stop`);
      setIsTracking(false);
      setActiveTimeEntry(null);
      fetchTaskDetails();
    } catch (error) {
      console.error('Error stopping timer:', error);
      alert('Failed to stop timer');
    }
  };

  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const getTotalTimeSpent = () => {
    return timeEntries.reduce((total, entry) => total + (entry.duration || 0), 0);
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-container">
          <div className="modal-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (!task) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container task-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{task.title}</h2>
          <div className="modal-header-actions">
            <button className="btn-icon" onClick={() => onEdit(task)} title="Edit">
              <FaEdit />
            </button>
            <button className="btn-icon btn-danger" onClick={handleDelete} title="Delete">
              <FaTrash />
            </button>
            <button className="btn-close" onClick={onClose}>
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="task-details-tabs">
          <button
            className={`tab ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            Details
          </button>
          <button
            className={`tab ${activeTab === 'comments' ? 'active' : ''}`}
            onClick={() => setActiveTab('comments')}
          >
            <FaComments /> Comments ({comments.length})
          </button>
          <button
            className={`tab ${activeTab === 'attachments' ? 'active' : ''}`}
            onClick={() => setActiveTab('attachments')}
          >
            <FaPaperclip /> Attachments ({attachments.length})
          </button>
          <button
            className={`tab ${activeTab === 'time' ? 'active' : ''}`}
            onClick={() => setActiveTab('time')}
          >
            <FaClock /> Time Tracking
          </button>
        </div>

        <div className="modal-body">
          {activeTab === 'details' && (
            <div className="task-details-content">
              <div className="task-meta-grid">
                <div className="meta-item">
                  <label>Status:</label>
                  <span className="status-badge" style={{ backgroundColor: task.status_color }}>
                    {task.status_name}
                  </span>
                </div>
                <div className="meta-item">
                  <label>Priority:</label>
                  <span className="priority-badge" style={{ backgroundColor: task.priority_color }}>
                    {task.priority_name}
                  </span>
                </div>
                <div className="meta-item">
                  <label>Assignee:</label>
                  <span><FaUser /> {task.assignee_name || 'Unassigned'}</span>
                </div>
                <div className="meta-item">
                  <label>Due Date:</label>
                  <span>{task.due_date ? new Date(task.due_date).toLocaleString() : 'No due date'}</span>
                </div>
                <div className="meta-item">
                  <label>Category:</label>
                  <span>{task.category || 'Uncategorized'}</span>
                </div>
                <div className="meta-item">
                  <label>Estimated:</label>
                  <span>{task.estimated_hours ? `${task.estimated_hours}h` : 'Not set'}</span>
                </div>
              </div>

              {task.description && (
                <div className="task-description">
                  <h4>Description</h4>
                  <p>{task.description}</p>
                </div>
              )}

              {task.tags && task.tags.length > 0 && (
                <div className="task-tags">
                  <h4>Tags</h4>
                  <div className="tag-list">
                    {task.tags.map((tag, idx) => (
                      <span key={idx} className="task-tag" style={{ backgroundColor: tag.color }}>
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'comments' && (
            <div className="task-comments">
              <form onSubmit={handleAddComment} className="comment-form">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="comment-textarea"
                  rows="3"
                />
                <button type="submit" className="btn btn-primary">Post Comment</button>
              </form>

              <div className="comments-list">
                {comments.map(comment => (
                  <div key={comment.id} className="comment-item">
                    <div className="comment-header">
                      <strong>{comment.user_name}</strong>
                      <span className="comment-time">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="comment-content">{comment.content}</p>
                  </div>
                ))}
                {comments.length === 0 && (
                  <p className="empty-message">No comments yet</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'attachments' && (
            <div className="task-attachments">
              <p className="info-message">File attachments feature coming soon</p>
              {attachments.length === 0 && (
                <p className="empty-message">No attachments</p>
              )}
            </div>
          )}

          {activeTab === 'time' && (
            <div className="task-time-tracking">
              <div className="time-tracker-controls">
                {!isTracking ? (
                  <button onClick={handleStartTimer} className="btn btn-success">
                    <FaPlay /> Start Timer
                  </button>
                ) : (
                  <button onClick={handleStopTimer} className="btn btn-danger">
                    <FaStop /> Stop Timer
                  </button>
                )}
                <div className="total-time">
                  Total Time: {formatDuration(getTotalTimeSpent())}
                </div>
              </div>

              <div className="time-entries-list">
                {timeEntries.map(entry => (
                  <div key={entry.id} className="time-entry-item">
                    <div className="time-entry-date">
                      {new Date(entry.start_time).toLocaleString()}
                    </div>
                    <div className="time-entry-duration">
                      {entry.end_time ? formatDuration(entry.duration) : 'In progress...'}
                    </div>
                  </div>
                ))}
                {timeEntries.length === 0 && (
                  <p className="empty-message">No time entries</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailsModal;
